import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import classnames from 'classnames';
import { generateFullName, getIndicatorColor } from 'utils/badgeHelpers';
import { intervalToDuration } from 'date-fns';
import { ccmPageReadOnlyStatuses } from 'constants/patient_statuses';
import HeartIcon from '../icons/heartIcon';
import { capitalize } from '../../../utils';
import ccmStatusDescription from '../../../utils/ccmStatusDescription';
import { parseDateSafe } from '../../../utils/timeHelper';

const PatientNameRiskStatusBadge = ({
  profile,
  patientDetailsLoading,
  riskScore,
}) => {
  const checkIsReadOnly = (ccmStatus) =>
    ccmPageReadOnlyStatuses?.some((status) => status === ccmStatus);
  const {
    birthDate,
    title,
    firstName,
    middleName,
    lastName,
    holdingStatus,
    ccmStatus,
  } = profile;
  const isRiskScore = Number.isInteger(riskScore);
  const today = new Date();
  const endDate = parseDateSafe(holdingStatus?.endTime);
  const remainingDays =
    endDate &&
    intervalToDuration({
      start: today,
      end: endDate,
    })?.days;

  const { isUnenrolled, descriptionByStatus } = useMemo(
    () => ({
      isUnenrolled: checkIsReadOnly(ccmStatus),
      descriptionByStatus: ccmStatusDescription(ccmStatus),
    }),
    [ccmStatus],
  );

  return (
    <div
      className={classnames(
        'name-badge',
        {
          'slide-in-badge': !patientDetailsLoading,
        },
        { 'slide-in-badge--inactive': isUnenrolled },
      )}
    >
      {!patientDetailsLoading && (
        <>
          <div className="d-flex">
            <div className="fullName">
              {generateFullName(title, firstName, middleName, lastName)}
              {'\xa0\xa0'}|{'\xa0\xa0'}
              {birthDate?.replaceAll('-', '/')}
              {holdingStatus?.status && holdingStatus.status !== 'active' && (
                <>
                  {'\xa0\xa0'}|{'\xa0\xa0'}
                  <span>
                    {capitalize(holdingStatus?.status)}: ({remainingDays + 1}{' '}
                    days)
                  </span>
                </>
              )}
              {isRiskScore && (
                <>
                  {'\xa0\xa0'}|{'\xa0\xa0'}
                  <span style={{ marginRight: '1rem' }}>
                    Risk Score <span>{riskScore}</span>
                  </span>
                </>
              )}
            </div>
            {isRiskScore && (
              <div className="risk-score__indicator">
                <HeartIcon color={getIndicatorColor(riskScore)} />
              </div>
            )}
          </div>
          <div className="holding-status-flag__wrapper">
            {descriptionByStatus}

            {profile.pcmStatus && (
              <div className="holding-status-flag">PCM</div>
            )}
            {profile.ccmHoldingStatus && (
              <div
                className="holding-status-flag"
                title={profile.ccmHoldingStatusStartDate}
              >
                Temp. Hold
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

PatientNameRiskStatusBadge.defaultProps = {
  patientDetailsLoading: false,
};

PatientNameRiskStatusBadge.propTypes = {
  profile: PropTypes.shape().isRequired,
  riskScore: PropTypes.number,
  patientDetailsLoading: PropTypes.bool,
};

export default PatientNameRiskStatusBadge;
